import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  createCycleProcedure,
  updateCycleProcedure,
  deleteCycleProcedure,
  reorderCycleProcedures,
  type CreateProcedurePayload,
  type UpdateProcedurePayload,
} from '../../../api/procedures';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

async function invalidateTreatmentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['treatments', patientId] }),
    queryClient.invalidateQueries({ queryKey: ['patient', patientId] }),
  ]);
}

export function useCreateCycleProcedure(patientId: string, treatmentId: string, cycleId: string) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateProcedurePayload) =>
      createCycleProcedure(patientId, treatmentId, cycleId, payload, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, patientId),
  });
}

export function useUpdateCycleProcedure(
  patientId: string,
  treatmentId: string,
  cycleId: string,
) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: { procedureId: string; payload: UpdateProcedurePayload }) =>
      updateCycleProcedure(patientId, treatmentId, cycleId, args.procedureId, args.payload, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, patientId),
  });
}

export function useDeleteCycleProcedure(
  patientId: string,
  treatmentId: string,
  cycleId: string,
) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (procedureId: string) =>
      deleteCycleProcedure(patientId, treatmentId, cycleId, procedureId, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, patientId),
  });
}

export function useReorderCycleProcedures(
  patientId: string,
  treatmentId: string,
  cycleId: string,
) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderedProcedureIds: readonly string[]) =>
      reorderCycleProcedures(patientId, treatmentId, cycleId, orderedProcedureIds, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, patientId),
  });
}
